import React, { useEffect, useState, useRef } from "react";
import { Form, Input, InputNumber, Select, Row as AntRow, Col, Alert, Button, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { States } from "../../constant/states";
import { BLACK, WARNING_400, ERROR_400 } from "../../constant/colors";
import {
  selectDoctorDuplicates,
  selectUser,
  selectEnvironment,
  selectProvidersAllDoctors,
  selectProvidersFacility,
  selectUsers
} from "../../store/selector";
import ACTIONS from "../../store/action";
import { StatusTag, Section, SectionTitle } from "./shared/components";
import { formatAddress, getUserName } from "./shared/utils";
import { dateTimeFormatter } from "../../utils/date";
import { trackPendoEvent } from "../../utils/pendo/pendoUtils";
import { formatPhoneNumberWhileTyping } from "../../utils/formaters";
import { createNpiValidator, createEmailValidator } from "../../utils/validators";
import { filterSort } from "../../utils/sort";

const { Option } = Select;
const { Text } = Typography;

export const phoneNumberLengthRegEx = /^\d{10}$/;

const AddressCell = styled(Text)`
  display: block;
  width: 100%;
  font-size: 14px;
  color: ${BLACK};
`;

const FacilityCell = styled(Text)`
  display: block;
  width: 100%;
  font-size: 14px;
  color: ${BLACK};
`;

const DuplicateTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  table-layout: fixed;

  th {
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: ${BLACK};
    padding: 8px 0;
    border-bottom: 1px solid ${WARNING_400};
  }

  th:nth-child(1) {
    width: 100px;
  }
  th:nth-child(2) {
    width: 120px;
  }
  th:nth-child(3) {
    width: 120px;
  }
  th:nth-child(4) {
    width: 160px;
  }
  th:nth-child(5) {
    width: 100px;
    padding-left: 16px;
    padding-right: 16px;
  }
  td:nth-child(5) {
    padding-left: 16px;
    padding-right: 16px;
  }
  th:nth-child(6) {
    width: 140px;
  }
  th:nth-child(7) {
    width: 140px;
    padding-left: 16px;
  }
  td:nth-child(7) {
    padding-left: 16px;
  }
  th:nth-child(8) {
    width: 60px;
    text-align: center;
  }

  td:nth-child(8) {
    text-align: center;
  }

  td {
    padding: 8px 0;
    font-size: 14px;
    color: ${BLACK};
    border-bottom: 1px solid ${WARNING_400};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ViewButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 14px;
  text-decoration: underline;
`;

const AlertHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 25px !important;
  margin-right: 155px;

  .ant-alert-content {
    flex: 1;
    width: 100%;
  }

  .ant-alert-description {
    margin-left: -40px !important;
    width: calc(100% + 39px) !important;
  }
`;

const ErrorDuplicateTable = styled(DuplicateTable)`
  th {
    border-bottom: 1px solid ${ERROR_400};
  }

  td {
    border-bottom: 1px solid ${ERROR_400};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-weight: 600;
    display: inline-flex;
    align-items: center;
  }

  /* Hide the default red asterisk on the left */
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    display: none !important;
  }

  /* Add black asterisk on the right */
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
    display: inline-block !important;
    content: "*" !important;
    color: ${BLACK} !important;
    font-size: 14px !important;
  }

  input,
  .ant-select-selection-item {
    font-size: 16px;
  }

  .ant-select-selector {
    height: 35px !important;

    .ant-select-selection-item {
      line-height: 35px !important;
    }
  }

  .ant-select-single:not(.ant-select-customize-input) .ant-select-selector::after {
    line-height: 35px !important;
  }
`;

const Row = styled(AntRow)`
  padding-right: 155px;
`;

const PhysicianForm = ({ form, initialValues, isEdit, onDuplicateViewClick, onFieldsChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const duplicates = useSelector(selectDoctorDuplicates);
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const allDoctors = useSelector(selectProvidersAllDoctors) || [];
  const facilities = useSelector(selectProvidersFacility) || [];
  const users = useSelector(selectUsers) || {};
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isExactDuplicate, setIsExactDuplicate] = useState(false);
  const hasEverExpanded = useRef(false);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  // Check for duplicates when form loads with initial values (for edit mode)
  // This ensures duplicate warning shows if editing a physician with same NPI as another
  useEffect(() => {
    if (initialValues && allDoctors.length > 0) {
      // Small delay to ensure form values are set
      const timer = setTimeout(() => {
        const npi = form.getFieldValue("npi");
        if (npi) {
          // Trigger duplicate check by calling handleFieldChange logic directly
          checkForDuplicates();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [initialValues, allDoctors.length, form]);

  // Helper to normalize string for comparison (trim, lowercase, remove extra spaces)
  const normalizeValue = (value) => {
    if (!value || value === "null" || value === "undefined") return "";
    return value.toString().trim().toLowerCase().replace(/\s+/g, " ");
  };

  // Check if two Practice Location values match (both empty = match, both same value = match)
  const practiceLocationMatches = (formValue, doctorValue) => {
    const normalizedFormValue = normalizeValue(formValue);
    const normalizedDoctorValue = normalizeValue(doctorValue);
    return normalizedFormValue === normalizedDoctorValue;
  };

  // Check if full Practice Location section matches (address, suiteNumber, city, state, zipCode, facility)
  const hasPracticeLocationMatch = (doctor) => {
    const formAddress = form.getFieldValue("address");
    const formSuiteNumber = form.getFieldValue("suiteNumber");
    const formCity = form.getFieldValue("city");
    const formState = form.getFieldValue("state");
    const formZipCode = form.getFieldValue("zipCode");
    const formFacility = form.getFieldValue("facility");

    // Check if user has entered ANY Practice Location field
    const hasAnyPracticeLocationValue =
      normalizeValue(formAddress) ||
      normalizeValue(formSuiteNumber) ||
      normalizeValue(formCity) ||
      normalizeValue(formState) ||
      normalizeValue(formZipCode) ||
      normalizeValue(formFacility);

    // If no Practice Location fields are filled, no exact duplicate possible
    if (!hasAnyPracticeLocationValue) {
      return false;
    }

    // All Practice Location fields must match for exact duplicate
    return (
      practiceLocationMatches(formAddress, doctor.address) &&
      practiceLocationMatches(formSuiteNumber, doctor.suiteNumber) &&
      practiceLocationMatches(formCity, doctor.city) &&
      practiceLocationMatches(formState, doctor.state) &&
      practiceLocationMatches(formZipCode, doctor.zipCode) &&
      practiceLocationMatches(formFacility, doctor.facility)
    );
  };

  // Check for NPI duplicates locally using the doctors already in Redux store
  // Same logic for both create and edit modes - just check if NPI exists in another physician
  // Also check for exact duplicates (NPI + full Practice Location match)
  const checkForDuplicates = () => {
    const npi = form.getFieldValue("npi");

    if (!npi) {
      // No NPI - no warning
      setIsExactDuplicate(false);
      dispatch(ACTIONS.setDoctorExactDuplicate(false));
      if (duplicates && duplicates.length > 0) {
        dispatch(ACTIONS.setDoctorDuplicates([]));
      }
      return;
    }

    // Exclude current physician when editing
    const excludeId = isEdit && initialValues?.id ? Number(initialValues.id) : null;

    // Find all doctors with matching NPI (excluding current if editing)
    const npiDuplicates = allDoctors.filter((doctor) => {
      // Exclude the current physician if editing
      if (excludeId && Number(doctor.id) === excludeId) {
        return false;
      }
      // Check NPI match
      return doctor.npi === npi;
    });

    // Check if any NPI duplicate also has the same full Practice Location (exact duplicate)
    // Practice Location = address, suiteNumber, city, state, zipCode, facility
    const exactDuplicates = npiDuplicates.filter((doctor) => hasPracticeLocationMatch(doctor));
    const hasExactDuplicate = exactDuplicates.length > 0;

    // Update exact duplicate state (both local and Redux)
    setIsExactDuplicate(hasExactDuplicate);
    dispatch(ACTIONS.setDoctorExactDuplicate(hasExactDuplicate));

    // Update duplicates state - show only exact duplicates if any, otherwise all NPI duplicates
    const duplicatesToShow = hasExactDuplicate ? exactDuplicates : npiDuplicates;
    const duplicateIds = duplicatesToShow
      .map((d) => d.id)
      .sort()
      .join(",");
    const currentDuplicateIds = (duplicates || [])
      .map((d) => d.id)
      .sort()
      .join(",");

    if (duplicateIds !== currentDuplicateIds) {
      dispatch(ACTIONS.setDoctorDuplicates(duplicatesToShow));
    }
  };

  // Wrapper for onChange handlers - calls the duplicate check
  const handleFieldChange = () => {
    checkForDuplicates();
  };

  useEffect(() => {
    if (duplicates && duplicates.length > 0) {
      setShowDuplicateWarning(true);
      // Only auto-collapse if user has never expanded it
      if (!hasEverExpanded.current) {
        setIsCollapsed(true);
      }
    } else {
      setShowDuplicateWarning(false);
      // Reset when duplicates go away
      hasEverExpanded.current = false;
      setIsCollapsed(true);
    }
  }, [duplicates]);

  const validateNPI = createNpiValidator(
    t("physician_management.form.validation_npi_required"),
    t("physician_management.form.validation_npi_format")
  );

  const validateEmail = createEmailValidator(t("physician_management.form.validation_email_invalid"));

  const validateAlphanumeric = (_, value) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
      return Promise.reject(new Error(t("physician_management.form.validation_alphanumeric")));
    }
    return Promise.resolve();
  };

  const validateAlphanumericNoSpaces = (_, value) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^[a-zA-Z0-9]+$/.test(value)) {
      return Promise.reject(new Error(t("physician_management.form.validation_alphanumeric_no_spaces")));
    }
    return Promise.resolve();
  };

  const validateZipCode = (_, value) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^\d{5}$/.test(value)) {
      return Promise.reject(new Error(t("physician_management.form.validation_zip_format")));
    }
    return Promise.resolve();
  };

  const handleViewDuplicate = (physicianId) => {
    try {
      trackPendoEvent(dispatch, "Physician_ViewSpecificDuplicate", {
        username: user?.email,
        account,
        duplicatePhysicianId: physicianId
      });
    } catch (error) {
      console.error("Error dispatching physician view specific duplicate Pendo event:", error);
    }
    // Notify parent to update the popup description
    if (onDuplicateViewClick) {
      onDuplicateViewClick();
    }
    // Defer navigation to next tick to allow React to process the popup description state update
    // This ensures the correct message is shown when the unsaved changes popup appears
    setTimeout(() => {
      history.push(`/physicians-settings/${physicianId}`);
    }, 0);
  };

  const handleToggleDuplicateWarning = () => {
    const willExpand = isCollapsed;

    if (willExpand) {
      // Mark that user has expanded it - never auto-collapse again
      hasEverExpanded.current = true;

      try {
        const eventName = isExactDuplicate ? "Physician_ViewDuplicates_NPIAddress" : "Physician_ViewDuplicates_NPI";
        trackPendoEvent(dispatch, eventName, {
          username: user?.email,
          account,
          duplicateCount: duplicates.length
        });
      } catch (error) {
        console.error("Error dispatching physician view duplicates Pendo event:", error);
      }
    }

    setIsCollapsed(!isCollapsed);
  };

  return (
    <StyledForm form={form} layout="vertical" onFieldsChange={onFieldsChange}>
      <Section>
        <SectionTitle>{t("physician_management.form.section_main")}</SectionTitle>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="npi"
              label={t("physician_management.form.label_npi")}
              rules={[{ required: true, validator: validateNPI }]}
              className={showDuplicateWarning && duplicates.length > 0 ? "has-duplicate" : ""}
            >
              <Input
                placeholder={t("physician_management.form.placeholder_npi")}
                maxLength={10}
                onChange={handleFieldChange}
                style={
                  showDuplicateWarning && duplicates.length > 0
                    ? isExactDuplicate
                      ? {
                          border: `1px solid ${ERROR_400}`,
                          boxShadow: `0 0 0 2px rgba(249, 112, 102, 0.2)`,
                          borderRadius: 6
                        }
                      : {
                          border: `1px solid ${WARNING_400}`,
                          boxShadow: `0 0 0 2px rgba(253, 176, 34, 0.2)`,
                          borderRadius: 6
                        }
                    : {}
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="firstName"
              label={t("physician_management.form.label_first_name")}
              rules={[{ required: true, message: t("physician_management.form.validation_first_name_required") }]}
            >
              <Input placeholder={t("physician_management.form.placeholder_first_name")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="lastName"
              label={t("physician_management.form.label_last_name")}
              rules={[{ required: true, message: t("physician_management.form.validation_last_name_required") }]}
            >
              <Input placeholder={t("physician_management.form.placeholder_last_name")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="title" label={t("physician_management.form.label_title")}>
              <Input placeholder={t("physician_management.form.placeholder_title")} />
            </Form.Item>
          </Col>
        </Row>
        {showDuplicateWarning && duplicates.length > 0 && (
          <StyledAlert
            id={isExactDuplicate ? "Physician_ViewDuplicates_NPI_Address" : "Physician_ViewDuplicates_NPI"}
            message={
              <AlertHeader onClick={handleToggleDuplicateWarning}>
                <span>
                  {isExactDuplicate
                    ? t("physician_management.form.duplicate_warning_exact")
                    : t("physician_management.form.duplicate_warning_partial")}
                </span>
                {isCollapsed ? <DownOutlined /> : <UpOutlined />}
              </AlertHeader>
            }
            description={
              !isCollapsed &&
              (isExactDuplicate ? (
                <ErrorDuplicateTable>
                  <thead>
                    <tr>
                      <th>{t("physician_management.form.duplicate_table_npi")}</th>
                      <th>{t("physician_management.form.duplicate_table_name")}</th>
                      <th>{t("physician_management.list.facility")}</th>
                      <th>{t("physician_management.form.duplicate_table_address")}</th>
                      <th></th>
                      <th>{t("physician_management.form.duplicate_table_last_updated")}</th>
                      <th>{t("physician_management.form.duplicate_table_created")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicates.map((dup) => (
                      <tr key={dup.id}>
                        <td>{dup.npi || "-"}</td>
                        <td>{dup.name || "-"}</td>
                        <td>
                          <FacilityCell ellipsis={{ tooltip: dup.facility || "-" }}>{dup.facility || "-"}</FacilityCell>
                        </td>
                        <td>
                          <AddressCell ellipsis={{ tooltip: formatAddress(dup) || "-" }}>
                            {formatAddress(dup) || "-"}
                          </AddressCell>
                        </td>
                        <td>
                          <StatusTag className={dup.isActive ? "active-tag" : "inactive-tag"}>
                            {dup.isActive
                              ? t("physician_management.list.active_status")
                              : t("physician_management.list.inactive_status")}
                          </StatusTag>
                        </td>
                        <td>
                          {dup.userUpdatedAt
                            ? `${getUserName(
                                users,
                                dup.updatedBy,
                                t("physician_management.detail.default_user")
                              )} ${dateTimeFormatter(dup.userUpdatedAt)}`
                            : "-"}
                        </td>
                        <td>
                          {dup.createdAt
                            ? `${getUserName(
                                users,
                                dup.createdBy,
                                t("physician_management.detail.default_user")
                              )} ${dateTimeFormatter(dup.createdAt)}`
                            : "-"}
                        </td>
                        <td>
                          <ViewButton
                            id="Physician_ViewSpecificDuplicate"
                            type="link"
                            onClick={() => handleViewDuplicate(dup.id)}
                          >
                            {t("physician_management.form.duplicate_table_view")}
                          </ViewButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </ErrorDuplicateTable>
              ) : (
                <DuplicateTable>
                  <thead>
                    <tr>
                      <th>{t("physician_management.form.duplicate_table_npi")}</th>
                      <th>{t("physician_management.form.duplicate_table_name")}</th>
                      <th>{t("physician_management.list.facility")}</th>
                      <th>{t("physician_management.form.duplicate_table_address")}</th>
                      <th></th>
                      <th>{t("physician_management.form.duplicate_table_last_updated")}</th>
                      <th>{t("physician_management.form.duplicate_table_created")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicates.map((dup) => (
                      <tr key={dup.id}>
                        <td>{dup.npi || "-"}</td>
                        <td>{dup.name || "-"}</td>
                        <td>
                          <FacilityCell ellipsis={{ tooltip: dup.facility || "-" }}>{dup.facility || "-"}</FacilityCell>
                        </td>
                        <td>
                          <AddressCell ellipsis={{ tooltip: formatAddress(dup) || "-" }}>
                            {formatAddress(dup) || "-"}
                          </AddressCell>
                        </td>
                        <td>
                          <StatusTag className={dup.isActive ? "active-tag" : "inactive-tag"}>
                            {dup.isActive
                              ? t("physician_management.list.active_status")
                              : t("physician_management.list.inactive_status")}
                          </StatusTag>
                        </td>
                        <td>
                          {dup.userUpdatedAt
                            ? `${getUserName(
                                users,
                                dup.updatedBy,
                                t("physician_management.detail.default_user")
                              )} ${dateTimeFormatter(dup.userUpdatedAt)}`
                            : "-"}
                        </td>
                        <td>
                          {dup.createdAt
                            ? `${getUserName(
                                users,
                                dup.createdBy,
                                t("physician_management.detail.default_user")
                              )} ${dateTimeFormatter(dup.createdAt)}`
                            : "-"}
                        </td>
                        <td>
                          <ViewButton
                            id="Physician_ViewSpecificDuplicate"
                            type="link"
                            onClick={() => handleViewDuplicate(dup.id)}
                          >
                            {t("physician_management.form.duplicate_table_view")}
                          </ViewButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DuplicateTable>
              ))
            }
            type={isExactDuplicate ? "error" : "warning"}
            showIcon
            closable={false}
          />
        )}
      </Section>

      <Section>
        <SectionTitle>{t("physician_management.form.section_practice")}</SectionTitle>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="address" label={t("physician_management.form.label_address")}>
              <Input
                placeholder={t("physician_management.form.placeholder_address")}
                onChange={handleFieldChange}
                style={
                  showDuplicateWarning && duplicates.length > 0 && isExactDuplicate
                    ? {
                        border: `1px solid ${ERROR_400}`,
                        boxShadow: `0 0 0 2px rgba(249, 112, 102, 0.2)`,
                        borderRadius: 6
                      }
                    : {}
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="suiteNumber" label={t("physician_management.form.label_suite")}>
              <Input
                placeholder={t("physician_management.form.placeholder_suite")}
                onChange={handleFieldChange}
                style={
                  showDuplicateWarning && duplicates.length > 0 && isExactDuplicate
                    ? {
                        border: `1px solid ${ERROR_400}`,
                        boxShadow: `0 0 0 2px rgba(249, 112, 102, 0.2)`,
                        borderRadius: 6
                      }
                    : {}
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="facility" label={t("physician_management.form.label_facility")}>
              <Select
                showSearch
                allowClear
                placeholder={t("physician_management.form.placeholder_facility")}
                onChange={handleFieldChange}
                filterSort={filterSort}
                filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                style={
                  showDuplicateWarning && duplicates.length > 0 && isExactDuplicate
                    ? {
                        border: `1px solid ${ERROR_400}`,
                        boxShadow: `0 0 0 2px rgba(249, 112, 102, 0.2)`,
                        borderRadius: 6
                      }
                    : {}
                }
              >
                {facilities.map((facility) => (
                  <Option key={facility.id} value={facility.name} localSortKey={facility.name}>
                    {facility.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="city"
              label={t("physician_management.form.label_city")}
              rules={[{ validator: validateAlphanumeric }]}
            >
              <Input
                placeholder={t("physician_management.form.placeholder_city")}
                onChange={handleFieldChange}
                style={
                  showDuplicateWarning && duplicates.length > 0 && isExactDuplicate
                    ? {
                        border: `1px solid ${ERROR_400}`,
                        boxShadow: `0 0 0 2px rgba(249, 112, 102, 0.2)`,
                        borderRadius: 6
                      }
                    : {}
                }
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="state" label={t("physician_management.form.label_state")}>
              <Select
                showSearch
                placeholder={t("physician_management.form.placeholder_state")}
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                onChange={handleFieldChange}
                style={
                  showDuplicateWarning && duplicates.length > 0 && isExactDuplicate
                    ? {
                        border: `1px solid ${ERROR_400}`,
                        boxShadow: `0 0 0 2px rgba(249, 112, 102, 0.2)`,
                        borderRadius: 6
                      }
                    : {}
                }
              >
                {States &&
                  Object.values(States).map((state, index) => (
                    <Option key={index} value={state.code} className="state-option">
                      {state.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="zipCode"
              label={t("physician_management.form.label_zip")}
              rules={[{ validator: validateZipCode }]}
            >
              <Input
                placeholder={t("physician_management.form.placeholder_zip")}
                maxLength={5}
                onChange={handleFieldChange}
                style={
                  showDuplicateWarning && duplicates.length > 0 && isExactDuplicate
                    ? {
                        border: `1px solid ${ERROR_400}`,
                        boxShadow: `0 0 0 2px rgba(249, 112, 102, 0.2)`,
                        borderRadius: 6
                      }
                    : {}
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("physician_management.form.section_contact")}</SectionTitle>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="phone"
              label={t("physician_management.form.label_phone")}
              rules={[
                {
                  pattern: phoneNumberLengthRegEx,
                  message: t("physician_management.form.phone_enter_digits")
                }
              ]}
            >
              <InputNumber
                maxLength={14}
                controls={false}
                placeholder={t("physician_management.form.phone_pattern")}
                formatter={(value) => formatPhoneNumberWhileTyping(value)}
                parser={(value) => value.replace(/[^0-9]/g, "")}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="fax"
              label={t("physician_management.form.label_fax")}
              rules={[
                {
                  pattern: phoneNumberLengthRegEx,
                  message: t("physician_management.form.phone_enter_digits")
                }
              ]}
            >
              <InputNumber
                maxLength={14}
                controls={false}
                placeholder={t("physician_management.form.phone_pattern")}
                formatter={(value) => formatPhoneNumberWhileTyping(value)}
                parser={(value) => value.replace(/[^0-9]/g, "")}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="email"
              label={t("physician_management.form.label_email")}
              rules={[{ validator: validateEmail }]}
            >
              <Input placeholder={t("physician_management.form.placeholder_email")} />
            </Form.Item>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("physician_management.form.section_personal")}</SectionTitle>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="stateLicense"
              label={t("physician_management.form.label_state_license")}
              rules={[{ validator: validateAlphanumericNoSpaces }]}
            >
              <Input placeholder={t("physician_management.form.placeholder_state_license")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="ptan"
              label={t("physician_management.form.label_ptan")}
              rules={[{ validator: validateAlphanumericNoSpaces }]}
            >
              <Input placeholder={t("physician_management.form.placeholder_ptan")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="dea"
              label={t("physician_management.form.label_dea")}
              rules={[{ validator: validateAlphanumericNoSpaces }]}
            >
              <Input placeholder={t("physician_management.form.placeholder_dea")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="taxId"
              label={t("physician_management.form.label_tax_id")}
              rules={[{ validator: validateAlphanumericNoSpaces }]}
            >
              <Input placeholder={t("physician_management.form.placeholder_tax_id")} />
            </Form.Item>
          </Col>
        </Row>
      </Section>
    </StyledForm>
  );
};

export { PhysicianForm };
